import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import { alpha } from '@mui/material/styles';

import React from 'react';

import { AiCallForCallPopDTO } from '_lib/api/apiTypes.shim';
import { WarningAlert } from '_lib/components/Alert/Alert';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { Stack } from '_lib/components/Stack/Stack';
import { Typography } from '_lib/components/Typography/Typography';
import { AiCallTranscriptList } from '_lib/featureComponents/AiCall/AiCallTranscriptList';
import { HeaderBox } from '_lib/featureComponents/HeaderBox/HeaderBox';
import { HeaderTextBlock } from '_lib/featureComponents/HeaderTextBlock/HeaderTextBlock';
import { IconTypography } from '_lib/featureComponents/IconTypography/IconTypography';
import { TextBlockTypography } from '_lib/featureComponents/TextBoxTypography/TextBoxTypography';
import { formatPhone } from '_lib/utils/helpers';

const CPI_TRANSFER_GREETING =
  'Hi, my name is _______. Give me just a moment to pull up notes from your previous conversation so I can better assist you.';

type AiCallProps = {
  aiCall: AiCallForCallPopDTO['aiCall'];
  /** NOTE: not currently used, but likely will be at some point in the future */
  company: AiCallForCallPopDTO['company'];
};

export const AiCall = (props: AiCallProps): React.ReactElement => {
  const { aiCall, company } = props;

  return (
    <FlexBox sx={{ flexDirection: 'column', justifyContent: 'space-between', height: 'stretch' }}>
      <Stack spacing={2} sx={{ padding: 2 }} useFlexGap>
        <FlexBox
          sx={(theme) => ({
            flexDirection: 'column',
            backgroundColor: alpha(theme.palette.infoSecondary.main, 0.1),
            borderLeft: `4px solid ${theme.palette.infoSecondary.main}`,
            padding: 2
          })}>
          <Typography variant='subtitle2' sx={{ fontWeight: 600, marginBottom: 1 }} text='CPI Transfer Greeting' />
          <Typography variant='body2'>{CPI_TRANSFER_GREETING}</Typography>
        </FlexBox>
        {aiCall.quickSummary && <HeaderTextBlock headerText='CPI Call Summary' text={aiCall.quickSummary} />}
        {aiCall.companySwitched && (
          <HeaderBox headerText='Sister Company Info'>
            <Stack spacing={0}>
              <TextBlockTypography text='Switched: Yes' />
              {aiCall.originalCompany && <TextBlockTypography text={`Original Company: ${aiCall.originalCompany}`} />}
            </Stack>
          </HeaderBox>
        )}
        <HeaderBox headerText='Caller Provided Info'>
          <Stack spacing={0.5}>
            <IconTypography startIcon={<PersonIcon color='primary' />} text={aiCall.caller?.name || 'No Caller Name'} />
            <IconTypography
              startIcon={<PhoneIcon color='primary' />}
              text={aiCall.caller?.phoneNumber ? formatPhone(aiCall.caller.phoneNumber) : 'No Caller Phone Number'}
            />
          </Stack>
        </HeaderBox>
        {!aiCall.transcriptMessages.length && (
          <WarningAlert>
            <Typography>No transcript messages available for this CPI Call.</Typography>
          </WarningAlert>
        )}
        {aiCall.transcriptMessages.length > 0 && (
          <HeaderBox headerText='Full Transcript:'>
            <AiCallTranscriptList
              transcriptMessages={aiCall.transcriptMessages}
              timezone={company.timezone}
              sx={(theme) => ({
                maxHeight: '450px',
                overflowY: 'auto',
                width: '100%',
                marginTop: 1,
                padding: 1.5,
                border: `2px solid ${theme.palette.divider}`,
                borderRadius: '4px',
                backgroundColor: theme.palette.background.paper
              })}
            />
          </HeaderBox>
        )}
      </Stack>
    </FlexBox>
  );
};
